import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { ReactNode } from 'react';
import { useUnreadNotificationsCount } from '_clean-code/api/hooks/useNotifications';
import type { AlertCardData } from '_clean-code/components/AlertCard';

interface AlertsDrawerContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  unreadCount: number;
  selectedAlert: AlertCardData | null;
  setSelectedAlert: (alert: AlertCardData | null) => void;
}

const AlertsDrawerContext = createContext<AlertsDrawerContextValue | null>(
  null
);

export function AlertsDrawerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<AlertCardData | null>(
    null
  );

  const { data: unreadData } = useUnreadNotificationsCount();
  const unreadCount = unreadData?.totalItems ?? 0;

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      unreadCount,
      selectedAlert,
      setSelectedAlert
    }),
    [isOpen, open, close, toggle, unreadCount, selectedAlert]
  );

  return (
    <AlertsDrawerContext.Provider value={value}>
      {children}
    </AlertsDrawerContext.Provider>
  );
}

export function useAlertsDrawer(): AlertsDrawerContextValue {
  const ctx = useContext(AlertsDrawerContext);
  if (!ctx) {
    throw new Error('useAlertsDrawer must be used within AlertsDrawerProvider');
  }
  return ctx;
}
